import { useState, useEffect } from "react";
import { collection, query, where, orderBy, onSnapshot, Timestamp, limit } from "firebase/firestore";
import { db } from "@Services/firebase/firestore";

export interface InboxConversation {
    id: string; // Document ID
    page_id: string;
    user_id: string;
    user_name: string;
    user_pic: string;
    last_message: string;
    last_message_at: Timestamp;
    unread_count: number;
    is_archived?: boolean;
}

export interface ChatMessage {
    id: string;
    text: string;
    type: string;
    sender_id: string;
    is_from_business: boolean;
    created_at: Timestamp;
}

// Helper to determine if we should look for messages in all accounts or a specific one
export function useInboxConversations(pageIds: string[] | string | null | undefined) {
    const [conversations, setConversations] = useState<InboxConversation[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [limitCount, setLimitCount] = useState<number>(20);

    // Reset limit when pageIds change
    useEffect(() => {
        setLimitCount(20);
    }, [JSON.stringify(pageIds)]);

    const loadMore = () => {
        setLimitCount(prev => prev + 20);
    };

    useEffect(() => {
        if (!pageIds || (Array.isArray(pageIds) && pageIds.length === 0)) {
            setConversations([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        let unsubscribes: (() => void)[] = [];

        if (Array.isArray(pageIds)) {
            // Firestore 'in' query supports max 10 values.
            // We need to chunk the array into batches of 10.
            const chunkSize = 10;
            const chunks = [];
            for (let i = 0; i < pageIds.length; i += chunkSize) {
                chunks.push(pageIds.slice(i, i + chunkSize));
            }

            // We will maintain a map of results to easily merge updates
            // Map<chunkIndex, conversations[]>
            const resultsMap = new Map<number, InboxConversation[]>();

            chunks.forEach((chunk, index) => {
                const q = query(
                    collection(db, "inbox_conversations"),
                    where("page_id", "in", chunk),
                    orderBy("last_message_at", "desc"),
                    limit(limitCount)
                );

                const unsub = onSnapshot(q, (snapshot) => {
                    const convs = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })) as InboxConversation[];

                    resultsMap.set(index, convs);

                    // Merge all results and sort
                    let allConvs: InboxConversation[] = [];
                    resultsMap.forEach((chunkConvs) => {
                        allConvs = [...allConvs, ...chunkConvs];
                    });

                    // Sort again client-side because merging chunks breaks order
                    allConvs.sort((a, b) => {
                        return (b.last_message_at?.toMillis() || 0) - (a.last_message_at?.toMillis() || 0);
                    });

                    setConversations(allConvs);
                    setLoading(false);
                }, (error) => {
                    console.error("Error fetching conversations chunk:", error);
                    setLoading(false);
                });

                unsubscribes.push(unsub);
            });

        } else {
            // Single page selected
            const q = query(
                collection(db, "inbox_conversations"),
                where("page_id", "==", pageIds),
                orderBy("last_message_at", "desc"),
                limit(limitCount)
            );

            const unsub = onSnapshot(q, (snapshot) => {
                const convs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as InboxConversation[];
                setConversations(convs);
                setLoading(false);
            }, (error) => {
                console.error("Error fetching conversations:", error);
                setLoading(false);
            });

            unsubscribes.push(unsub);
        }

        return () => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, [JSON.stringify(pageIds), limitCount]);

    return { conversations, loading, loadMore };
}

export function useChatMessages(conversationId: string | null | undefined) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!conversationId) {
            setMessages([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const messagesRef = collection(db, "inbox_conversations", conversationId, "messages");
        const q = query(
            messagesRef,
            orderBy("created_at", "asc")
            // Not adding limit to chat messages yet as discussed (usually small volume per chat), 
            // but for production it's recommended to limit history too.
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as ChatMessage[];
            setMessages(msgs);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching messages:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [conversationId]);

    return { messages, loading };
}
